import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {ElementMouseEvent} from 'util/Types';

export const DEFAULT_SIDEBAR_WIDTH = 350;
export const MINIMUM_SIDEBAR_WIDTH = 250;

type SidebarDragState = {
  dragStartSidebarWidth: number;
} & ({isResizing: true; startMouseX: number} | {isResizing: false});

interface SidebarDragStateInput {
  sidebarSide: 'left' | 'right';
  defaultWidth?: number;
  maxWidth?: number;
  minWidth?: number;
}

interface SidebarWrapperProps {
  onMouseMove: (e: ElementMouseEvent) => void;
  onMouseUp: (e: ElementMouseEvent) => void;
}

interface DragHandleProps {
  onMouseDown: (e: ElementMouseEvent) => void;
}

interface SidebarDragStateOutput {
  sidebarWidth: number;
  isResizing: boolean;
  sidebarWrapperProps: SidebarWrapperProps;
  dragHandleProps: DragHandleProps;
}

/**
 * Helper hook to manage the drag interaction and state for UI that resizes a sidebar.
 *
 * @example
 * ```
 *  const {sidebarWidth, isResizing, sidebarWrapperProps, dragHandleProps} = useSidebarDragState({
 *    sidebarSide: 'left',
 *    defaultWidth: 300,
 *  });
 *
 *  return <div
 *      // Useful to override the cursor to `col-resize` on the parent element
 *      style={isResizing ? {cursor: 'col-resize'} : undefined}
 *      {...sidebarWrapperProps}
 *  >
 *    // sidebar
 *    <div style={{width: sidebarWidth}} />
 *
 *    // separator / drag handle
 *    <SidebarResizeSeparator isResizing={isResizing} dragHandleProps={dragHandleProps} />
 *  </div>
 * ```
 */
export default function useSidebarDragState({
  sidebarSide,
  defaultWidth = DEFAULT_SIDEBAR_WIDTH,
  maxWidth = Infinity,
  minWidth = MINIMUM_SIDEBAR_WIDTH,
}: SidebarDragStateInput): SidebarDragStateOutput {
  const [sidebarWidth, setSidebarWidth] = useState(defaultWidth);
  const [sidebarDragState, setSidebarDragState] = useState<SidebarDragState>({
    isResizing: false,
    dragStartSidebarWidth: defaultWidth,
  });

  const getMouseX = useCallback(
    (e: ElementMouseEvent): number => (sidebarSide === 'right' ? e.clientX : -1 * e.clientX),
    [sidebarSide]
  );

  const startSidebarResize = useCallback(
    (e: ElementMouseEvent): void => {
      if (sidebarDragState.isResizing) {
        return;
      }
      setSidebarDragState({
        isResizing: true,
        startMouseX: getMouseX(e),
        dragStartSidebarWidth: sidebarWidth,
      });
      e.preventDefault();
    },
    [sidebarDragState.isResizing, getMouseX, sidebarWidth]
  );

  const sidebarResize = useCallback(
    (e: ElementMouseEvent): void => {
      if (!sidebarDragState.isResizing) {
        return;
      }

      const mouseDeltaX = Math.floor(getMouseX(e) - sidebarDragState.startMouseX);
      setSidebarWidth(
        Math.min(maxWidth, Math.max(minWidth, sidebarDragState.dragStartSidebarWidth - mouseDeltaX))
      );
      e.preventDefault();
    },
    [sidebarDragState, getMouseX, maxWidth, minWidth]
  );

  const endSidebarResize = useCallback(
    (e: ElementMouseEvent): void => {
      if (!sidebarDragState.isResizing) {
        return;
      }
      setSidebarDragState({isResizing: false, dragStartSidebarWidth: sidebarWidth});
      e.preventDefault();
    },
    [sidebarDragState.isResizing, sidebarWidth]
  );

  const sidebarWrapperProps = useMemo(
    () => ({onMouseMove: sidebarResize, onMouseUp: endSidebarResize}),
    [sidebarResize, endSidebarResize]
  );

  const dragHandleProps = useMemo(() => ({onMouseDown: startSidebarResize}), [startSidebarResize]);

  return {
    isResizing: sidebarDragState.isResizing,
    dragHandleProps,
    sidebarWrapperProps,
    sidebarWidth,
  };
}

const useStyles = createStyles(() => ({
  sidebarSeparator: {
    flex: '0 0 2px',
    backgroundColor: Color.GRAY_200,
    cursor: 'col-resize',
    position: 'relative',
    ':hover': {
      backgroundColor: Color.GRAY_400,
    },
  },
  sidebarSeparatorActive: {
    backgroundColor: Color.BLUE_400,
    ':hover': {
      backgroundColor: Color.BLUE_400,
    },
  },
}));

type SidebarResizeSeparatorProps = Pick<SidebarDragStateOutput, 'dragHandleProps' | 'isResizing'>;

export function SidebarResizeSeparator({
  dragHandleProps,
  isResizing,
}: SidebarResizeSeparatorProps): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <div
      role="separator"
      className={cx(classes.sidebarSeparator, {[classes.sidebarSeparatorActive]: isResizing})}
      {...dragHandleProps}
    />
  );
}
